import { Alert, Box, Button, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { IconAlertCircle, IconArrowLeft } from "@tabler/icons-react";
import { MfaChallengeError } from "@canucci/base/auth";
import { supportEmail } from "@canucci/base/env";
import type { ReactNode } from "react";
import { useCallback, useState, type FormEvent } from "react";
import { MFA_CHALLENGE_I18N_KEYS } from "../loginPageI18n";
import type { LoginTr } from "../useLoginTranslations";
import styles from "../LoginPage.module.css";
import { AuthSplitLayout } from "./AuthSplitLayout";
import layoutStyles from "./AuthSplitLayout.module.css";

export interface LoginMfaChallengeViewProps {
  onSubmit: (otpCode: string) => Promise<void>;
  onCancel: () => void;
  logo?: ReactNode;
  tr: LoginTr;
}

export function LoginMfaChallengeView({ onSubmit, onCancel, logo, tr }: LoginMfaChallengeViewProps) {
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const trimmed = otpCode.trim();
      if (!/^\d{6}$/.test(trimmed)) {
        setError(tr("login.mfa.invalidCodeFormat", "Enter the 6-digit code from the app on your phone."));
        return;
      }
      setError("");
      setSubmitting(true);
      try {
        await onSubmit(trimmed);
      } catch (err: unknown) {
        if (err instanceof MfaChallengeError) {
          setError(tr(MFA_CHALLENGE_I18N_KEYS[err.kind], err.message));
        } else {
          setError(tr("login.mfa.genericError", "Could not verify the code. Please try again."));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [onSubmit, otpCode, tr],
  );

  return (
    <AuthSplitLayout brandMode="mfaChallenge" logo={logo} tr={tr}>
      <UnstyledButton type="button" className={layoutStyles.backLink} onClick={onCancel}>
        <IconArrowLeft size={16} aria-hidden />
        {tr("login.mfa.backToSignIn", "Back to sign in")}
      </UnstyledButton>

      <Box component="h1" className={layoutStyles.formTitle} mb={0}>
        {tr("login.mfa.title", "Two-step verification")}
      </Box>
      <Box className={layoutStyles.formSubtitle}>
        {tr("login.mfa.subtitle", "Enter the 6-digit code from the app on your phone.")}
      </Box>

      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md" mt="lg">
          {error ? (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title={tr("login.errorTitle", "Error")}>
              {error}
            </Alert>
          ) : null}
          <TextInput
            label={tr("login.mfa.codeLabel", "Authentication code")}
            placeholder="000000"
            value={otpCode}
            onChange={(event) => {
              setOtpCode(event.currentTarget.value.replace(/\D/g, "").slice(0, 6));
              if (error) setError("");
            }}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            className={styles.authInput}
          />
          <Button type="submit" loading={submitting} fullWidth>
            {tr("login.mfa.verify", "Verify")}
          </Button>
          <Text size="xs" c="dimmed" ta="center">
            {tr(
              "login.mfa.lostAccess",
              "No longer have your phone? Contact {{supportEmail}} and we'll help you back in.",
              { supportEmail: supportEmail() },
            )}
          </Text>
        </Stack>
      </form>
    </AuthSplitLayout>
  );
}
